// tslint:disable
import {compareDesc} from 'date-fns';
import {types} from 'mobx-state-tree';
import * as consts from '../../consts';
import * as api from '../../services/api';
import ChatChannelModel, {
  ChatChannel,
  ChatChannelKind
} from '../chatChannelModel';
import WithFeedbackModel, {
  WithFeedbackActions
} from '../composables/withFeedbackModel';

export type ChatViews = {
  sortedChannels: ChatChannel[];
  nextOffset: number;
  nextFilterOffset: number;
};

export type ChatActions = {
  loadChannels: (kind: ChatChannelKind, offset?: number) => void;
  filterChannels: (
    params: any,
    kind?: ChatChannelKind,
    offset?: number
  ) => void;
  loadSingleChannel: (channelId: number) => Promise<void>;
  receiveChannels: (channels: ChatChannel[]) => void;
  receiveFilterResults: (channels: ChatChannel[]) => void;
  setCurrentChannel: (channelId: number) => void;
  setCurrentChannelType: (channelType: string) => void;
  unsetCurrentChannel: () => void;
  // tslint:disable-next-line:no-any
  createChannel: (
    userIds: number[],
    unique?: boolean,
    onSuccess?: (data: any) => void
  ) => void;
  clearPagination: () => void;
  clearFilterPagination: () => void;
};

const ChatModel = types.compose(
  types
    .model({
      channels: types.map(ChatChannelModel),
      currentChannel: types.maybe(types.reference(ChatChannelModel)),
      currentChannelType: types.optional(types.string, 'external'),
      loading: types.optional(types.boolean, false),
      loaded: types.optional(types.boolean, false),
      offset: types.optional(types.number, 0),

      filterResults: types.array(ChatChannelModel),
      filtering: types.optional(types.boolean, false),
      filterLoaded: types.optional(types.boolean, false),
      filterOffset: types.optional(types.number, 0),
      lastFilterByListingId: types.maybeNull(types.number),
      lastFilterByTeamId: types.maybeNull(types.number),
      lastQ: types.maybeNull(types.string),
      lastSearchType: types.maybeNull(types.string)
    })
    .views<ChatViews>(self => {
      return {
        get sortedChannels() {
          const {channels} = self;
          const channelsList = Array.from(channels.values());

          return channelsList
            .filter(c => c.type.toLowerCase() === self.currentChannelType)
            .sort((c1: ChatChannel, c2: ChatChannel) => {
              return compareDesc(c1.lastMessageAt!, c2.lastMessageAt!);
            });
        },
        get nextOffset() {
          return self.offset + consts.PAGINATION__DEFAULT_PER_PAGE;
        },
        get nextFilterOffset() {
          return self.filterOffset + consts.PAGINATION__DEFAULT_PER_PAGE;
        }
      };
    })
    .actions<ChatActions>(self => {
      // tslint:disable-next-line:no-any
      const withActions = (self as any) as ChatActions;

      // tslint:disable-next-line:no-any
      const withFeedback = (self as any) as WithFeedbackActions;

      return {
        loadChannels: async function(
          kind: ChatChannelKind,
          offset?: number,
          limit?: number
        ) {
          self.loading = true;
          const perPage = consts.PAGINATION__DEFAULT_PER_PAGE || limit;
          const {status, data} = await api.getChannels(kind, perPage, offset);

          if (status === 200 && data) {
            if (!data.channels || data.channels.length === 0) {
              return;
            }

            withActions.receiveChannels(data.channels);
            self.offset = self.offset + perPage;
          }

          self.loading = false;
          self.loaded = true;
        },

        filterChannels: async function(
          params: any,
          kind: ChatChannelKind = ChatChannelKind.EXTERNAL,
          offset?: number,
          limit?: number
        ) {
          self.filtering = true;

          const filterByListingId = Number(params.listingId);
          const filterByTeamId = Number(params.teamId);

          if (
            filterByListingId !== self.lastFilterByListingId ||
            filterByTeamId !== self.lastFilterByTeamId ||
            params.q !== self.lastQ ||
            params.searchType !== self.lastSearchType
          ) {
            self.filterResults.clear();
          }

          self.lastFilterByListingId = filterByListingId;
          self.lastFilterByTeamId = filterByTeamId;
          self.lastQ = params.q;
          self.lastSearchType = params.lastSearchType;

          const perPage = consts.PAGINATION__DEFAULT_PER_PAGE || limit;
          const {status, data} = await api.filterChannels(
            params,
            kind,
            perPage,
            offset
          );

          if (status === 200 && data) {
            if (!data.channels || data.channels.length === 0) {
              return;
            }

            withActions.receiveFilterResults(data.channels);
            self.filterOffset += perPage;
          }

          self.filtering = false;
          self.filterLoaded = true;
        },

        receiveFilterResults: (data: any) => {
          data.forEach((channelData: any) => {
            self.filterResults.push(channelData);
          });
        },

        loadSingleChannel: async function(channelId: number) {
          self.loading = true;
          const {status, data} = await api.getSingleChannel(channelId);

          if (status === 200 && data) {
            withActions.receiveChannels([data.channel]);
          } else {
            withFeedback.sendErrorFeedback(
              'An error has occurred while trying to load this channel. Please try again later.'
            );
          }

          self.loading = false;
          self.loaded = true;
        },

        receiveChannels: function(channels: ChatChannel | ChatChannel[]) {
          if (Array.isArray(channels)) {
            const receivingChannels = channels.reduce(
              (results: {[id: string]: ChatChannel}, receiving) => {
                const channel = self.channels.get(String(receiving.id));
                return (
                  (results[receiving.id] = {
                    ...channel,
                    ...receiving,
                    loaded: true
                  }),
                  results
                );
                // tslint:disable-next-line:align
              },
              {}
            );

            self.channels.merge(receivingChannels);
          } else {
            const channel = channels as ChatChannel;
            self.channels.set(String(channel.id), {...channel, loaded: true});
          }
        },

        setCurrentChannel: async function (channelId: number) {
          const cached = self.channels.get(String(channelId));

          if (cached) {
            self.currentChannel = cached;
          } else {
            await withActions.loadSingleChannel(channelId);
            self.currentChannel = self.channels.get(String(channelId))!;
          }

          if (self.currentChannel) {
            self.currentChannel.loadCachedMessages();
          }
        },

        unsetCurrentChannel: function() {
          self.currentChannel = undefined;
        },

        setCurrentChannelType: function(channelType: string) {
          self.currentChannelType = channelType;
        },

        // tslint:disable-next-line:no-any
        createChannel: async function(
          userIds: number[],
          unique?: boolean,
          onSuccess?: (data: any) => void
        ) {
          const response = await api.createChannel(userIds, unique);

          if (response.data && response.status === 200) {
            // tslint:disable-next-line
            console.log(response.data);

            if (typeof onSuccess === 'function') {
              onSuccess(response.data);
            }
          }
        },

        clearPagination: () => {
          self.offset = 0;
        },

        clearFilterPagination: () => {
          self.filterOffset = 0;
        }
      };
    }),
  WithFeedbackModel
);

export type ChatModelType = typeof ChatModel.Type;

export default ChatModel;



// WEBPACK FOOTER //
// ./src/models/chatModel/chatModel.ts